/**
 * Centralized frontend configuration.
 *
 * All brand constants and external URLs live here so that renaming the app
 * or updating a CTA link requires a single change in the .env file.
 */

// ── Brand ────────────────────────────────────────────────────────────────────
export const APP_NAME: string = import.meta.env.VITE_APP_NAME ?? 'Cartley'

// ── Cartley brand palette ─────────────────────────────────────────────────────
export const BRAND = {
  /** Cartley primary red — used for CTAs, highlights, active states. */
  red: '#ef042d',
  /** Cartley dark navy — used for headers, nav, primary text accents. */
  navy: '#01114f',
  /** Cartley red hover (10% darker). */
  redHover: '#c8002a',
  /** Cartley navy hover (slightly lighter). */
  navyLight: '#0d1f6e',
} as const

// ── External URLs ────────────────────────────────────────────────────────────
export const CONSULTATION_URL: string =
  import.meta.env.VITE_CONSULTATION_URL ?? 'https://cartley.com/consultation'

// ── Footer / legal links ─────────────────────────────────────────────────────
export const FOOTER = {
  contactUrl: import.meta.env.VITE_CONTACT_URL ?? 'https://cartley.com/contact',
  termsUrl: import.meta.env.VITE_TERMS_URL ?? 'https://cartley.com/terms',
  privacyUrl: import.meta.env.VITE_PRIVACY_URL ?? 'https://cartley.com/privacy',
  instagramUrl: import.meta.env.VITE_INSTAGRAM_URL ?? 'https://instagram.com/mycartley',
  facebookUrl: import.meta.env.VITE_FACEBOOK_URL ?? 'https://facebook.com/Cartley.US',
  youtubeUrl: import.meta.env.VITE_YOUTUBE_URL ?? 'https://youtube.com/@mycartley',
} as const

// ── CARTO basemap tiles (Leaflet) ────────────────────────────────────────────
/** CARTO now requires an API key; without it the tiles show an "API KEY REQUIRED" watermark. */
const CARTO_API_KEY: string = import.meta.env.VITE_CARTO_API_KEY ?? ''
export const CARTO_TILE_URL: string =
  'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png' +
  (CARTO_API_KEY ? `?key=${CARTO_API_KEY}` : '')
/** Attribution required by the CARTO Basemaps terms (CARTO + OpenStreetMap). */
export const CARTO_ATTRIBUTION: string =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> ' +
  '&copy; <a href="https://carto.com/attributions">CARTO</a>'

// ── Brandfetch ───────────────────────────────────────────────────────────────
/**
 * Client-side Brandfetch key — used only for the Logo Link CDN.
 * Safe to expose; cannot access the full Brand API.
 */
export const BRANDFETCH_CLIENT_KEY: string =
  import.meta.env.VITE_BRANDFETCH_CLIENT_KEY ?? ''
